import React, { useMemo, useState } from "react";
import { Eye } from "lucide-react";
import { PdfTypeIcon, Player } from "../AdminIcons";
import {
  useGetAllLecturesByModuleQuery,
  useGetAllPdfsByModuleQuery,
} from "../../services/course/courseApi";
import CourseMaterialsModal from "./modals/CourseMaterialsModal";



const materialsByTab = {
  pdf: [
    {
      id: 1,
      title: "HTML & CSS Reference Guide",
      meta: "2 Files • 18MB",
      isUploaded: true,
    },
    {
      id: 2,
      title: "CSS Layout Patterns Workbook",
      meta: "1 File • 9MB",
      isUploaded: true,
    },
    {
      id: 3,
      title: "Responsive Design Notes",
      meta: "Not uploaded",
      isUploaded: false,
    },
  ],
};

const CourseMaterialsSection = ({ courseId }) => {
  const [activeTab, setActiveTab] = useState("recorded");
  const [selectedMaterial, setSelectedMaterial] = useState(null);

  const { data: pdfResponse } = useGetAllPdfsByModuleQuery(courseId, {
    skip: !courseId,
  });

  const { data: lecturesResponse } = useGetAllLecturesByModuleQuery(courseId, {
    skip: !courseId,
  });

  const pdfMaterials = useMemo(() => {
    const responseData = pdfResponse?.data ?? pdfResponse;
    const rawItems = Array.isArray(responseData)
      ? responseData
      : Array.isArray(responseData?.data)
        ? responseData.data
        : Array.isArray(responseData?.pdfMaterials)
          ? responseData.pdfMaterials
          : Array.isArray(responseData?.modules)
            ? responseData.modules
            : [];

    return rawItems.map((item, index) => {
      const files = Array.isArray(item.files)
        ? item.files
        : Array.isArray(item.materials)
          ? item.materials
          : Array.isArray(item.pdfFiles)
            ? item.pdfFiles
            : [];

      const uploadedCount = typeof item.uploadedCount === "number"
        ? item.uploadedCount
        : typeof item.fileCount === "number"
          ? item.fileCount
          : files.length;

      const totalSizeLabel = item.totalSize || item.sizeLabel || item.metaSize || "";
      const title = item.moduleName || "";
      const meta = item.meta || (uploadedCount > 0 ? `${uploadedCount} File${uploadedCount === 1 ? "" : "s"}${totalSizeLabel ? ` • ${totalSizeLabel}` : ""}` : "Not uploaded");

      return {
        id: item._id || item.id || `${title}-${index}`,
        materials: item.materials,
        title,
        meta,
        isUploaded: uploadedCount > 0,
        materialType: "pdf",
      };
    });
  }, [pdfResponse]);

  const recordedMaterials = useMemo(() => {
    const responseData = lecturesResponse?.data ?? lecturesResponse;
    const rawItems = Array.isArray(responseData)
      ? responseData
      : Array.isArray(responseData?.data)
        ? responseData.data
        : [];

    return rawItems.map((item, index) => {
      const lectures = Array.isArray(item.lectures) ? item.lectures : [];
      const uploadedCount = lectures.length;
      const totalSizeLabel = item.totalSize || item.sizeLabel || item.metaSize || "";
      const title = item.moduleName || "";
      const meta = uploadedCount > 0
        ? `${uploadedCount} File${uploadedCount === 1 ? "" : "s"}${totalSizeLabel ? ` • ${totalSizeLabel}` : ""}`
        : "Not uploaded";

      return {
        id: item._id || item.id || `${title}-${index}`,
        materials: lectures,
        title,
        meta,
        isUploaded: uploadedCount > 0,
        materialType: "recorded",
      };
    });
  }, [lecturesResponse]);

  const activeMaterials = activeTab === "pdf"
    ? pdfMaterials
    : recordedMaterials;
  const uploadedCount = activeMaterials.filter((item) => item.isUploaded).length;
  const totalCount = activeMaterials.length;
  return (
    <section className="mt-6 overflow-hidden rounded-xl border border-[#E5E7EB] bg-white shadow-[0_1px_2px_#0000000D]">
      <header className="flex flex-col gap-3 border-b border-[#E5E7EB] bg-[#F9FAFB] px-4 py-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:px-6">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-base text-[#1F2937] sm:text-lg">
            Course Materials
          </h3>
          <span className="rounded-full bg-[#FEF9C3] px-2.5 py-0.5 text-xs text-[#854D0E]">
            Partially Complete
          </span>
        </div>

        <p className="text-sm text-[#6B7280]">{uploadedCount}/{totalCount} files uploaded</p>
      </header>

      <div className="px-4 py-6 sm:px-6">
        <div className="flex gap-6 overflow-x-auto border-b border-[#E5E7EB] sm:gap-8">
          <button
            type="button"
            onClick={() => setActiveTab("recorded")}
            className={`shrink-0 px-1 pb-[14px] pt-3 text-sm transition ${
              activeTab === "recorded"
                ? "border-b-2 border-[#923D8C] text-[#923D8C]"
                : "text-[#6B7280]"
            }`}
          >
            Recorded Lectures
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("pdf")}
            className={`shrink-0 px-1 pb-[14px] pt-3 text-sm transition ${
              activeTab === "pdf"
                ? "border-b-2 border-[#923D8C] text-[#923D8C]"
                : "text-[#6B7280]"
            }`}
          >
            PDF Materials
          </button>
        </div>

        <div className="mt-4 flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3">
          <h4 className="text-base text-[#1F2937]">
            {activeTab === "recorded" ? "Recorded Lectures" : "PDF Materials"}
          </h4>
          <p className="text-sm text-[#6B7280]">{uploadedCount}/{totalCount} uploaded</p>
        </div>

        <div className="mt-4 space-y-4">
          {activeMaterials.map((item) => {
            const isUploaded = item.isUploaded;

            return (
              <div
                key={item.id}
                className={`flex flex-col gap-4 rounded-lg border px-4 py-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between ${
                  isUploaded ? "border-[#E5E7EB] bg-[#F9FAFB]" : "border-dashed border-[#E5E7EB] bg-white"
                }`}
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#923D8C1A]">
                    {item.materialType === "pdf" ? <PdfTypeIcon /> : <Player />}
                  </span>

                  <div className="min-w-0">
                    <h5 className={`truncate text-sm sm:text-base ${isUploaded ? "text-[#000]" : "text-[#6B7280]"}`}>
                      {item.title}
                    </h5>
                    <p className="text-xs mt-1 text-[#6B7280]">{item.meta}</p>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-6 text-[#6B7280] sm:pl-4">
                  <button
                    type="button"
                    aria-label={`View ${item.title}`}
                    onClick={() => setSelectedMaterial(item)}
                    className="transition hover:text-[#4B5563]"
                  >
                    <Eye size={18} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <CourseMaterialsModal
        isOpen={Boolean(selectedMaterial)}
        item={selectedMaterial}
        courseId={courseId}
        materialType={activeTab}
        onClose={() => setSelectedMaterial(null)}
      />
    </section>
  );
};

export default CourseMaterialsSection;